import { Stack } from '@mui/material';
import { List, Placeholder } from '@telegram-apps/telegram-ui';
import dayjs from 'dayjs';
import { AnimatePresence } from 'framer-motion';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getTimeOfDay } from '@focuslee/shared';
import { RitualType } from '../../client/generated/graphql';
import { ListCategory, ListGoal, ListQuest } from '../../client/types';
import { useFeatureFlag } from '../../contexts/FeatureFlagContext';
import { dateSorter } from '../../helpers/datetime';
import { floatingTabbarScrollBottomPadWithSatellite } from '../../helpers/floatingTabbarLayout';
import { getCategorySummaries, dayTimeOptions } from '../../helpers/quest';
import { useDailyProgress } from '../../hooks/useDailyProgress';
import { useScrollRestore } from '../../hooks/useScrollRestore';
import { AnimatedDaySection } from '../animations/AnimatedDaySection';
import { FocusLeePlaceholderImage } from '../common/FocusLeePlaceholderImage';
import { QuestDaySection } from './QuestDaySection';
import { QuestDaySkeleton } from './skeleton/QuestDaySkeleton';
import { QuestSummary } from './QuestSummary';
import { TodayQuestProgress } from './TodayQuestProgress';

export function groupQuestsByTimeOfDay(
    quests: ListQuest[],
    selectedDate: dayjs.Dayjs,
    ritualsEnabled: boolean
): Record<string, ListQuest[]> {
    const questsGroupBy: Record<string, ListQuest[]> = {};
    const isToday = selectedDate.isSame(dayjs(), 'day');
    
    const sortedQuests = [...quests].sort((q1, q2) => dateSorter(q1.dueDate, q2.dueDate));
    
    for (const q of sortedQuests) {
        const dueDate = dayjs(q.dueDate);
        const dateMatch = dueDate.isSame(selectedDate, 'day');
        const timeOfDay = getTimeOfDay(q.dueDate);
        
        if (ritualsEnabled && q.ritualType === RitualType.Morning) {
            if ((timeOfDay === 'Overdue' && isToday) || dateMatch) {
                (questsGroupBy['MorningRitual'] = questsGroupBy['MorningRitual'] || []).push(q);
            }
        } else if (ritualsEnabled && q.ritualType === RitualType.Evening) {
            if ((timeOfDay === 'Overdue' && isToday) || dateMatch) {
                (questsGroupBy['EveningRitual'] = questsGroupBy['EveningRitual'] || []).push(q);
            }
        } else if (!q.ritualType) {
            if ((timeOfDay === 'Overdue' && isToday) || dateMatch) {
                (questsGroupBy[timeOfDay] = questsGroupBy[timeOfDay] || []).push(q);
            }
        }
    }
    
    return questsGroupBy;
}

export type QuestDayProps = {
    quests: ListQuest[];
    categoryMap: Record<string, ListCategory>;
    goalMap: Record<string, ListGoal>;
    selectedDate: dayjs.Dayjs;
    onItemClick: (q: ListQuest) => void;
    loading?: boolean;
};

export const QuestDay = ({ quests, categoryMap, goalMap, selectedDate, onItemClick, loading = false }: QuestDayProps) => {
    const { t } = useTranslation('common');
    const scrollKey = `questList_${selectedDate.format('YYYY-MM-DD')}`;
    const { setScrollRef, handleScroll } = useScrollRestore(scrollKey);
    const ritualsEnabled = useFeatureFlag('rituals');
    
    const questsGroupBy = useMemo(
        () => groupQuestsByTimeOfDay(quests, selectedDate, ritualsEnabled),
        [quests, selectedDate, ritualsEnabled]
    );

    const noQuests = Object.entries(questsGroupBy)
        .reduce((acc, g) => acc + g[1].length, 0) === 0;
    const { data: dailyProgressData, loading: dailyProgressLoading } = useDailyProgress(questsGroupBy);
    const isToday = selectedDate.isSame(dayjs(), 'day');
    const goalsEnabled = useFeatureFlag('goals');
    const doneSum = isToday && dailyProgressData ? Object.values(dailyProgressData.progressMap).reduce((sum, p) => sum + p.done, 0) : 0;
    const totalSum = isToday && dailyProgressData ? Object.values(dailyProgressData.progressMap).reduce((sum, p) => sum + p.total, 0) : 0;
    const summaryPercentage = totalSum > 0 ? Math.round((doneSum / totalSum) * 100) : 0;
    const allQuestsCompleted = isToday && !!dailyProgressData?.allQuestsCompleted;
    
    if (loading)
        return <QuestDaySkeleton />;
    
    return (
        <AnimatePresence mode="wait" initial={false}>
            <AnimatedDaySection selectedDate={selectedDate}>
                {isToday && goalsEnabled && (
                    <TodayQuestProgress
                        doneSum={doneSum}
                        totalSum={totalSum}
                        summaryPercentage={summaryPercentage}
                        totalXP={dailyProgressData?.totalXP ?? 0}
                        completedQuests={dailyProgressData?.completedQuests ?? []}
                        allQuestsCompleted={allQuestsCompleted}
                        categoryMap={categoryMap}
                        loading={dailyProgressLoading}
                    />
                )}
                {isToday && !goalsEnabled && allQuestsCompleted && (
                    <Stack flex={1} minHeight={0} alignItems="center" justifyContent="center">
                        <QuestSummary
                            totalXP={dailyProgressData!.totalXP}
                            categorySummaries={getCategorySummaries(dailyProgressData!.completedQuests, categoryMap)}
                        />
                    </Stack>
                )}
                {!allQuestsCompleted && (noQuests && (!isToday || !dailyProgressData) ? (
                    <Stack direction="column" justifyContent="center" alignItems="center" height="100%">
                        <Placeholder
                            header={t('quest.empty.title')}
                            description={t('quest.empty.description')}
                            style={{ padding: 0 }}
                        >
                            <FocusLeePlaceholderImage kind="quests" />
                        </Placeholder>
                    </Stack>
                ) :
                    <Stack
                        ref={setScrollRef}
                        onScroll={handleScroll}
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            overflowY: 'auto',
                            WebkitOverflowScrolling: 'touch',
                        }}
                    >
                        <List
                            style={{
                                background: 'var(--tgui--secondary_bg_color)',
                                padding: 10,
                                paddingBottom: floatingTabbarScrollBottomPadWithSatellite,
                            }}
                        >
                            {dayTimeOptions.filter(o => o in questsGroupBy).map(o => (
                                <QuestDaySection
                                    key={o}
                                    sectionKey={o}
                                    quests={questsGroupBy[o]}
                                    isToday={isToday}
                                    dailyProgressData={dailyProgressData}
                                    categoryMap={categoryMap}
                                    goalMap={goalMap}
                                    onItemClick={onItemClick}
                                />
                            ))}
                        </List>
                    </Stack>
                )}
            </AnimatedDaySection>
        </AnimatePresence>
    );
};
